import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, ArrowRight, FileDown, Building2, Landmark, University, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILES = ['Entreprise opérant en Afrique', 'Banque / Prêteur', 'Institution / Bailleur de fonds', 'Autre'];

const PILLARS = [
    { icon: Building2, title: 'Entreprises', color: 'text-primary', items: ['Gouvernance multi-projets et multi-pays', 'Consolidation budgétaire multi-devises', 'Marque blanche & API REST'] },
    { icon: Landmark, title: 'Banques & prêteurs', color: 'text-secondary', items: ['Visibilité continue sur les projets financés', 'Project Score™ : maturité objectivée avant l\'engagement', 'Justificatifs de dépenses rattachés et vérifiables'] },
    { icon: University, title: 'Institutions & bailleurs', color: 'text-primary', items: ['Bénéficiaires hébergés, portefeuille consolidé', 'Reporting conforme AFD · BAD · Banque mondiale · UE', 'Renforcement de capacités via le Campus 360°'] },
];

export default function Partenaires() {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        full_name: '', email: '', organization: '', country: '', phone: '',
        profile: PROFILES[0], message: '', website: '',
    });
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        if (e) e.preventDefault();
        if (!form.full_name.trim() || !form.email.trim() || !form.organization.trim()) {
            toast.error('Nom, email et organisation sont requis.');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/access-requests/create`, {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                organization: form.organization.trim(),
                country: form.country.trim() || null,
                phone: form.phone.trim() || null,
                source: 'partenaires',
                sector: form.profile,
                message: `🤝 CONTACT PARTENAIRE · Profil : ${form.profile}`
                    + (form.message.trim() ? `\n\n${form.message.trim()}` : ''),
                website: form.website,
            });
            setSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        }
        setBusy(false);
    };

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="page-partenaires">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="partenaires-logo-home">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley" className="h-11 w-11 rounded-lg object-cover" />
                        <span className="font-heading text-2xl"><span className="text-foreground">African</span> <span className="text-primary">Valley</span></span>
                    </Link>
                    <Link to="/p/tarifs" className="text-sm text-foreground/70 hover:text-foreground">Voir les offres →</Link>
                </div>
            </header>

            <main className="flex-1">
                {/* Hero + téléchargements */}
                <section className="container-page py-14 lg:py-18">
                    <div className="max-w-3xl">
                        <div className="eyebrow mb-3">Entreprises · Banques · Institutions de financement</div>
                        <h1 className="font-heading text-4xl sm:text-5xl mb-5" data-testid="partenaires-title">
                            Piloter les projets. <span className="text-primary">Sécuriser les financements.</span> Mesurer l'impact.
                        </h1>
                        <p className="text-foreground/70 leading-relaxed mb-8">
                            African Valley 360° est la plateforme de confiance qui permet aux entreprises, banques et
                            institutions de financement de suivre l'exécution des projets financés, sécuriser le
                            décaissement, documenter chaque dépense et produire un reporting fiable pour chaque
                            partie prenante.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="/docs/African-Valley-360-Presentation-Partenaires.pdf" download data-testid="partenaires-download-pdf">
                                <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                                    <FileDown size={16} className="mr-2" /> Version PDF légère
                                </Button>
                            </a>
                        </div>
                    </div>
                </section>

                {/* 3 publics */}
                <section className="bg-surface border-y border-border/60">
                    <div className="container-page py-12">
                        <div className="grid md:grid-cols-3 gap-6">
                            {PILLARS.map(({ icon: Icon, title, color, items }) => (
                                <div key={title} className="card-soft p-6" data-testid={`partenaires-pillar-${title.split(' ')[0].toLowerCase()}`}>
                                    <Icon size={22} className={`${color} mb-3`} />
                                    <h2 className="font-heading text-lg mb-3">{title}</h2>
                                    <ul className="space-y-2 text-sm text-foreground/80">
                                        {items.map(it => (
                                            <li key={it} className="flex items-start gap-2">
                                                <Check size={14} className="text-secondary mt-0.5 shrink-0" /> {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Formulaire contact institutionnel */}
                <section className="container-page py-14">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        <div>
                            <h2 className="font-heading text-3xl mb-4">Un échange de cadrage, sans engagement.</h2>
                            <p className="text-foreground/70 leading-relaxed mb-6">
                                Décrivez votre contexte — portefeuille de projets, exigences de reporting, dispositif de
                                financement — et notre équipe vous propose une démonstration adaptée à votre profil
                                sous 24 h ouvrées.
                            </p>
                            <ul className="space-y-3 text-sm text-foreground/80 mb-8">
                                <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-secondary mt-0.5 shrink-0" /> Hébergement européen RGPD, journal d'audit immuable, isolation stricte par organisation.</li>
                                <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-secondary mt-0.5 shrink-0" /> Réversibilité totale : export complet de vos données à tout moment.</li>
                                <li className="flex items-start gap-2"><ShieldCheck size={16} className="text-secondary mt-0.5 shrink-0" /> Offre Institution : SLA contractuel, support dédié, option d'hébergement Afrique.</li>
                            </ul>
                            <div className="card-soft p-5 text-xs text-muted-foreground leading-relaxed" data-testid="partenaires-editeur">
                                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-foreground/60 mb-2">Éditeur de la plateforme</div>
                                <p>
                                    Dimitri M'FOUMOU-TITI (« DMF ») — Entrepreneur individuel<br />
                                    SIREN : 991 741 075 · SIRET : 991 741 075 00014 · Code APE : 90.03B<br />
                                    Immatriculé au Registre National des Entreprises le 23/09/2025<br />
                                    Siège social : 4 Cité Croix Saint Simon, 78100 Saint-Germain-en-Laye — France<br />
                                    Email : <a href="mailto:dimitri.mfoumou@gmail.com" className="text-primary hover:underline">dimitri.mfoumou@gmail.com</a> · Tél. : <a href="tel:+33698697558" className="text-primary hover:underline">+33 6 98 69 75 58</a>
                                </p>
                            </div>
                        </div>

                        <div className="card-soft p-7 lg:p-8" data-testid="partenaires-form-card">
                            {sent ? (
                                <div className="text-center py-10" data-testid="partenaires-success">
                                    <div className="text-5xl mb-4">✅</div>
                                    <h2 className="font-heading text-2xl mb-3">Demande bien reçue !</h2>
                                    <p className="text-foreground/70 mb-6 leading-relaxed">
                                        Notre équipe vous recontacte sous 24 h ouvrées pour préparer une démonstration
                                        adaptée à votre profil de partenaire.
                                    </p>
                                    <Button onClick={() => navigate('/')} variant="outline" data-testid="partenaires-back-home">
                                        Retour à l'accueil
                                    </Button>
                                </div>
                            ) : (
                                <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-4">
                                    <h2 className="font-heading text-xl mb-1">Contact institutionnel</h2>
                                    <p className="text-sm text-muted-foreground mb-4">Réponse sous 24 h ouvrées.</p>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="p-name">Nom & prénom *</Label>
                                            <Input id="p-name" value={form.full_name} onChange={set('full_name')} required data-testid="partenaires-name" />
                                        </div>
                                        <div>
                                            <Label htmlFor="p-email">Email professionnel *</Label>
                                            <Input id="p-email" type="email" value={form.email} onChange={set('email')} required data-testid="partenaires-email" />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="p-org">Organisation *</Label>
                                            <Input id="p-org" value={form.organization} onChange={set('organization')} required data-testid="partenaires-org" />
                                        </div>
                                        <div>
                                            <Label htmlFor="p-profile">Vous êtes</Label>
                                            <select id="p-profile" value={form.profile} onChange={set('profile')}
                                                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                                data-testid="partenaires-profile">
                                                {PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="p-country">Pays d'intervention</Label>
                                            <Input id="p-country" value={form.country} onChange={set('country')} placeholder="ex. Congo, Sénégal…" data-testid="partenaires-country" />
                                        </div>
                                        <div>
                                            <Label htmlFor="p-phone">Téléphone (WhatsApp accepté)</Label>
                                            <Input id="p-phone" value={form.phone} onChange={set('phone')} data-testid="partenaires-phone" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="p-msg">Votre contexte en quelques mots</Label>
                                        <Textarea id="p-msg" rows={3} value={form.message} onChange={set('message')}
                                            placeholder="Portefeuille, pays, exigences de reporting, calendrier…" data-testid="partenaires-message" />
                                    </div>
                                    <input type="text" value={form.website} onChange={set('website')} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                                    <Button type="button" onClick={() => submit()} disabled={busy} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="partenaires-submit">
                                        {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <ArrowRight size={15} className="mr-2" />}
                                        Demander un échange de cadrage
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
